import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User as UserIcon, LayoutDashboard, Users, PackagePlus, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { RegionSelector } from './RegionSelector';
import { Region } from '../../types/models';
interface AdminNavbarProps {
  selectedRegion?: Region;
  onRegionChange?: (region: Region) => void;
}
export function AdminNavbar({
  selectedRegion,
  onRegionChange
}: AdminNavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const navLinks = [{
    name: 'Dashboard',
    href: '/admin/dashboard',
    icon: <LayoutDashboard className="w-4 h-4" data-id="element-64" />
  }, {
    name: 'Manage Users',
    href: '/admin/users',
    icon: <Users className="w-4 h-4" data-id="element-65" />
  }, {
    name: 'Add Product',
    href: '/admin/products?action=add',
    icon: <PackagePlus className="w-4 h-4" data-id="element-66" />
  }, {
    name: 'Add Special',
    href: '/admin/products?action=add-special',
    icon: <Star className="w-4 h-4" data-id="element-67" />
  }];
  const isActive = (path: string) => {
    if (path.includes('?')) {
      return location.pathname + location.search === path;
    }
    return location.pathname === path;
  };
  return <nav className="bg-white border-b border-rose-100 sticky top-0 z-50" data-id="element-68">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-69">
        <div className="flex justify-between items-center h-20" data-id="element-70">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2" data-id="element-71">
            <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center" data-id="element-72">
              <span className="text-white font-serif font-bold text-xl" data-id="element-73">B</span>
            </div>
            <span className="font-serif text-xl font-bold text-gray-900" data-id="element-74">
              Bloom & Pop{' '}
              <span className="text-rose-500 text-sm font-sans font-medium ml-1" data-id="element-75">
                Admin
              </span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1" data-id="element-76">
            {navLinks.map(link => <Link key={link.name} to={link.href} data-id="element-77">
                <Button variant={isActive(link.href) ? 'primary' : 'ghost'} size="sm" leftIcon={link.icon} className={isActive(link.href) ? '' : 'text-gray-600 hover:text-rose-600 hover:bg-rose-50'} data-id="element-78">
                  {link.name}
                </Button>
              </Link>)}
          </div>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-4" data-id="element-79">
            {/* Region Selector for Main Admin */}
            {user?.adminType === 'main' && onRegionChange && <div className="border-r border-gray-200 pr-4 mr-2" data-id="element-80">
                <div className="flex items-center gap-2" data-id="element-81">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-82">
                    View:
                  </span>
                  <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-83">
                    <button onClick={() => onRegionChange('All')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedRegion === 'All' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-84">
                      All
                    </button>
                    <button onClick={() => onRegionChange('Gauteng')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedRegion === 'Gauteng' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-85">
                      GP
                    </button>
                    <button onClick={() => onRegionChange('Limpopo')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedRegion === 'Limpopo' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-86">
                      LP
                    </button>
                  </div>
                </div>
              </div>}

            <div className="flex items-center gap-3" data-id="element-87">
              <div className="text-right hidden xl:block" data-id="element-88">
                <p className="text-sm font-medium text-gray-900" data-id="element-89">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 capitalize" data-id="element-90">
                  {user?.role} {user?.region !== 'All' && `(${user?.region})`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50" leftIcon={<LogOut className="w-4 h-4" data-id="element-92" />} data-id="element-91">
                Logout
              </Button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center" data-id="element-93">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-md text-gray-600 hover:text-rose-500 focus:outline-none" data-id="element-94">
              {isOpen ? <X className="w-6 h-6" data-id="element-95" /> : <Menu className="w-6 h-6" data-id="element-96" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-97">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-white border-b border-rose-100 overflow-hidden" data-id="element-98">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-99">
              {user?.adminType === 'main' && onRegionChange && <div className="mb-4 p-4 bg-gray-50 rounded-lg" data-id="element-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-101">
                    Select Region View
                  </p>
                  <div className="grid grid-cols-3 gap-2" data-id="element-102">
                    {['All', 'Gauteng', 'Limpopo'].map(region => <button key={region} onClick={() => onRegionChange(region as Region)} className={`px-2 py-2 text-xs font-medium rounded-md border ${selectedRegion === region ? 'bg-white border-rose-200 text-rose-600 shadow-sm' : 'border-transparent text-gray-600 hover:bg-gray-100'}`} data-id="element-103">
                        {region}
                      </button>)}
                  </div>
                </div>}

              {navLinks.map(link => <Link key={link.name} to={link.href} onClick={() => setIsOpen(false)} data-id="element-104">
                  <div className={`flex items-center px-3 py-3 rounded-md text-base font-medium mb-1 ${isActive(link.href) ? 'bg-rose-50 text-rose-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-105">
                    <span className="mr-3" data-id="element-106">{link.icon}</span>
                    {link.name}
                  </div>
                </Link>)}

              <div className="pt-4 mt-4 border-t border-gray-100" data-id="element-107">
                <div className="flex items-center px-3 mb-3" data-id="element-108">
                  <div className="ml-3" data-id="element-109">
                    <p className="text-base font-medium text-gray-800" data-id="element-110">
                      {user?.name}
                    </p>
                    <p className="text-sm font-medium text-gray-500" data-id="element-111">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full justify-center text-rose-500 border-rose-200" onClick={() => {
              handleLogout();
              setIsOpen(false);
            }} data-id="element-112">
                  Logout
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}